import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import React, { FC, useCallback, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import * as R from 'ramda';
import { unstable_batchedUpdates } from 'react-dom';
import { action } from 'typesafe-actions';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { isDefined } from '@dxb-web-ui-framework/common/utils/Helper';
import { currencyService, favouriteTransfersService } from '../../../../../channels/secure-web/services';
import { useSelectedCustomerId } from '../../../../userProfile/src/contexts/UserInfo/hooks';
import { FavouriteTransfer } from '../../domain/FavouriteTransfer/entities';
import { Currency } from '../../domain/Currency/entities';
import { getRoutePath } from '../../../../content-composer/src/components/Routes/routePaths';
import { FavouriteTransfersActionEnum, FavouriteTransfersActionTypes, FavouriteTransfersListFilters } from './types';
import { FavouriteTransfersContext, FavouriteTransfersDispatchContext } from './FavouriteTransfersContext';
import { defaultState, initialFavouriteTransfersListFilters } from './defaultState';
import { FavouriteTransfersReducer } from './FavouriteTransfersReducer';

const FavouriteTransfersProvider: FC<{ shouldCallCurrenciesEndpoint?: boolean }> = ({
  children,
  shouldCallCurrenciesEndpoint = false,
}) => {
  // Get initial state values
  const [state, dispatch] = useReducer(FavouriteTransfersReducer, defaultState);
  const customerId = useSelectedCustomerId();
  const navigate = useNavigate();

  const shouldFetchFavouriteTransfers = !isEmpty(customerId);
  const {
    data: favouriteTransfersListData,
    mutate: favouriteTransfersRefetch,
    error: favouriteTransfersDataError,
    isLoading: isLoadingFavouriteTransfersList,
  } = useInvokeSWRService(
    favouriteTransfersService.GetFavouriteTransfers,
    {
      customerId,
      ...state.favouriteTransfersListFilters,
      name: state.favouriteTransfersListFilters?.name,
      debitAccountNumber: state.favouriteTransfersListFilters?.debitAccountNumber?.value,
      creditAccountNumber: state.favouriteTransfersListFilters?.creditAccountNumber,
      amountFrom: state.favouriteTransfersListFilters?.amountFrom ?? '0.00',
      amountTo: state.favouriteTransfersListFilters?.amountTo ?? '9999999999.99',
      currency: state.favouriteTransfersListFilters?.currency?.value,
      meta: state.meta,
    },
    shouldFetchFavouriteTransfers,
    'favouriteTransfersService.GetFavouriteTransfers',
  );

  const shouldEditFavouriteTransfer = Boolean(state.favouriteTransferToEdit);

  const {
    data: editFavouriteTransferData,
    error: editFavouriteTransferError,
    isLoading: isLoadingEditFavouriteTransfer,
  } = useInvokeSWRService(
    favouriteTransfersService.EditFavouriteTransfer,
    state.favouriteTransferToEdit === null
      ? {}
      : {
          customerId,
          id: state.favouriteTransferToEdit.id,
          name: state.favouriteTransferToEdit.name,
          shared: state.favouriteTransferToEdit.shared,
          data: {
            debitAccountNumber: state.favouriteTransferToEdit.debitAccountNumber,
            creditAccountNumber: state.favouriteTransferToEdit.creditAccountNumber,
            orderCurrency: state.favouriteTransferToEdit.orderCurrency,
            currencyDescription: state.favouriteTransferToEdit.currencyDescription,
            orderAmount: state.favouriteTransferToEdit.orderAmount,
            creditAccountType: state.favouriteTransferToEdit.creditAccountType,
            receiverComments: state.favouriteTransferToEdit.receiverComments,
            receiverBic: state.favouriteTransferToEdit.receiverBic,
            receiverAddress: state.favouriteTransferToEdit.receiverAddress,
            receiverFullName: state.favouriteTransferToEdit.receiverFullName,
            receiverFullNameMasked: state.favouriteTransferToEdit.receiverFullNameMasked,
            chargingMethod: state.favouriteTransferToEdit.chargingMethod,
            chargingMethodDescription: state.favouriteTransferToEdit.chargingMethodDescription,
            countrySn: state.favouriteTransferToEdit.countrySn,
            bankCountrySn: state.favouriteTransferToEdit.bankCountrySn,
            bankName: state.favouriteTransferToEdit.bankName,
            countryDescription: state.favouriteTransferToEdit.countryDescription,
            countryShortDescription: state.favouriteTransferToEdit.countryShortDescription,
            additionalGlobalOrdersDetailBicBankCode:
              state.favouriteTransferToEdit.additionalGlobalOrdersDetailBicBankCode,
            additionalGlobalOrdersDetailInnTaxPayerCode:
              state.favouriteTransferToEdit.additionalGlobalOrdersDetailInnTaxPayerCode,
            additionalGlobalOrdersDetailCorCbr: state.favouriteTransferToEdit.additionalGlobalOrdersDetailCorCbr,
            additionalGlobalOrdersDetailKppBeneficiary:
              state.favouriteTransferToEdit.additionalGlobalOrdersDetailKppBeneficiary,
            additionalGlobalOrdersDetailVocGhCode: state.favouriteTransferToEdit.additionalGlobalOrdersDetailVocGhCode,
            additionalGlobalOrdersDetailVocGdCode: state.favouriteTransferToEdit.additionalGlobalOrdersDetailVocGdCode,
            additionalGlobalOrdersDetailBopGh: state.favouriteTransferToEdit.additionalGlobalOrdersDetailBopGh,
            additionalGlobalOrdersDetailBopGd: state.favouriteTransferToEdit.additionalGlobalOrdersDetailBopGd,
            receiverSortCode: state.favouriteTransferToEdit.receiverSortCode,
            receiverCity: state.favouriteTransferToEdit.receiverCity,
          },
          meta: {
            timestamp: state.favouriteTransferToEdit?.timestamp,
          },
          successCallback: () => {
            favouriteTransfersRefetch && favouriteTransfersRefetch();
            dispatch(action(FavouriteTransfersActionTypes.SetFavouriteTransferToEdit, null));
            dispatch(
              action(FavouriteTransfersActionTypes.SetSelectedFavouriteTransfer, {
                selectedFavouriteTransfer: null,
                action: null,
              }),
            );
          },
        },
    shouldEditFavouriteTransfer,
    'favouriteTransfersService.EditFavouriteTransfer',
  );

  const shouldDeleteFavouriteTransfer = Boolean(state.favouriteTransferToDelete);
  const {
    data: deleteFavouriteTransferData,
    error: deleteFavouriteTransferError,
    isLoading: isLoadingDeleteFavouriteTransfer,
  } = useInvokeSWRService(
    favouriteTransfersService.DeleteFavouriteTransfer,
    state.favouriteTransferToDelete === null
      ? {}
      : {
          customerId,
          id: state.favouriteTransferToDelete.id,
          meta: {
            timestamp: state.favouriteTransferToDelete?.timestamp,
          },
          successCallback: () => {
            favouriteTransfersRefetch && favouriteTransfersRefetch();
            dispatch(action(FavouriteTransfersActionTypes.SetFavouriteTransferToDelete, null));
            dispatch(
              action(FavouriteTransfersActionTypes.SetSelectedFavouriteTransfer, {
                selectedFavouriteTransfer: null,
                action: null,
              }),
            );
          },
        },
    shouldDeleteFavouriteTransfer,
    'favouriteTransfersService.SetFavouriteTransferToDelete',
  );

  const shouldFetchCurrencies = Boolean(shouldCallCurrenciesEndpoint);
  const {
    data: currencies,
    error: currenciesError,
    isLoading: isLoadingCurrencies,
  } = useInvokeSWRService<Currency[]>(
    currencyService.GetCurrencies,
    {},
    shouldFetchCurrencies,
    'currencyService.GetCurrencies',
  );

  const onUseTemplateFavouriteTransfer = useCallback(
    (callback?: Function) => {
      if (callback) {
        callback();
      }
      if (!state.selectedFavouriteTransfer) {
        return;
      }
      navigate(getRoutePath({ routeKey: 'fund-transfer', customerId: customerId! }));
    },
    [state.selectedFavouriteTransfer, navigate, customerId],
  );

  const onEditFavouriteTransfer = useCallback(
    (alias?: string) => {
      if (!state.selectedFavouriteTransfer) {
        return;
      }
      dispatch(
        action(FavouriteTransfersActionTypes.SetFavouriteTransferToEdit, {
          ...state.selectedFavouriteTransfer,
          name: alias,
          timestamp: Date.now().toString(),
        }),
      );
      dispatch(
        action(FavouriteTransfersActionTypes.SetAction, {
          action: null,
        }),
      );
    },
    [state.selectedFavouriteTransfer],
  );

  const onDeleteFavouriteTransfer = useCallback(() => {
    if (!state.selectedFavouriteTransfer) {
      return;
    }

    dispatch(
      action(FavouriteTransfersActionTypes.SetFavouriteTransferToDelete, {
        ...state.selectedFavouriteTransfer,
        timestamp: Date.now().toString(),
        id: state.selectedFavouriteTransfer.id,
      }),
    );
    dispatch(
      action(FavouriteTransfersActionTypes.SetAction, {
        action: null,
      }),
    );
  }, [state.selectedFavouriteTransfer]);

  const onShareFavouriteTransfer = useCallback(() => {
    if (!state.selectedFavouriteTransfer) {
      return;
    }

    dispatch(
      action(FavouriteTransfersActionTypes.SetFavouriteTransferToEdit, {
        ...state.selectedFavouriteTransfer,
        timestamp: Date.now().toString(),
        shared: !state.selectedFavouriteTransfer.shared,
      }),
    );
    dispatch(
      action(FavouriteTransfersActionTypes.SetAction, {
        action: null,
      }),
    );
  }, [state.selectedFavouriteTransfer]);

  // Function passed as callback to tableGridConfig
  const onFavouriteTransferAction = useCallback(
    (selectedTransfer?: FavouriteTransfer, actionName?: FavouriteTransfersActionEnum) => {
      unstable_batchedUpdates(() => {
        if (!actionName) {
          dispatch(
            action(FavouriteTransfersActionTypes.SetSelectedFavouriteTransfer, {
              selectedFavouriteTransfer: null,
              action: null,
            }),
          );
        }

        if (!selectedTransfer) {
          return;
        }

        if (favouriteTransfersListData) {
          const favouriteTransferToSelect = R.find(
            favouriteTransfer => favouriteTransfer.id === selectedTransfer.id,
            favouriteTransfersListData.favoriteTransfersList,
          );

          if (!favouriteTransferToSelect) {
            dispatch(
              action(FavouriteTransfersActionTypes.SetSelectedFavouriteTransfer, {
                selectedFavouriteTransfer: null,
                action: null,
              }),
            );
            return;
          }

          if (
            actionName === FavouriteTransfersActionEnum.edit ||
            actionName === FavouriteTransfersActionEnum.delete ||
            actionName === FavouriteTransfersActionEnum.share ||
            actionName === FavouriteTransfersActionEnum.useTemplate
          ) {
            dispatch(
              action(FavouriteTransfersActionTypes.SetSelectedFavouriteTransfer, {
                selectedFavouriteTransfer: favouriteTransferToSelect,
                action: actionName,
              }),
            );
          }
        } else {
          dispatch(
            action(FavouriteTransfersActionTypes.SetSelectedFavouriteTransfer, {
              selectedFavouriteTransfer: null,
              action: null,
            }),
          );
        }
      });
    },
    [favouriteTransfersListData],
  );

  const onResetSelectedFavouriteTransfer = useCallback(() => {
    dispatch(
      action(FavouriteTransfersActionTypes.SetSelectedFavouriteTransfer, {
        selectedFavouriteTransfer: null,
        action: null,
      }),
    );
  }, []);

  const onResetFavouriteTransferListFilters = useCallback(() => {
    dispatch(
      action(FavouriteTransfersActionTypes.SetFavouriteTransfersListFilter, initialFavouriteTransfersListFilters),
    );
  }, []);

  const toggleFavouriteTransfersFilterDrawer = useCallback(() => {
    dispatch(
      action(
        FavouriteTransfersActionTypes.SetIsFavouriteTransfersFitlerDrawerOpen,
        !state.isFavouriteTransfersFilterDrawerOpen,
      ),
    );
  }, [state.isFavouriteTransfersFilterDrawerOpen]);

  const setFavouriteTransfersFilterDrawerClose = () => {
    dispatch(action(FavouriteTransfersActionTypes.SetIsFavouriteTransfersFitlerDrawerOpen, false));
  };

  const modifyFavouriteTransferListDataFilterDrawer = (filters: Partial<FavouriteTransfersListFilters>) => {
    if (!isDefined(filters.name) || isEmpty(filters.name)) {
      delete state.favouriteTransfersListFilters.name;
    }
    if (!isDefined(filters.debitAccountNumber)) {
      delete state.favouriteTransfersListFilters.debitAccountNumber;
    }
    if (!isDefined(filters.creditAccountNumber) || isEmpty(filters.creditAccountNumber)) {
      delete state.favouriteTransfersListFilters.creditAccountNumber;
    }
    if (!isDefined(filters.amountFrom) || isEmpty(filters.amountFrom)) {
      delete state.favouriteTransfersListFilters.amountFrom;
    }
    if (!isDefined(filters.amountTo) || isEmpty(filters.amountTo)) {
      delete state.favouriteTransfersListFilters.amountTo;
    }
    if (!isDefined(filters.currency)) {
      delete state.favouriteTransfersListFilters.currency;
    }
  };

  const onFavouriteTransferListFiltersChange = (
    filters: Partial<FavouriteTransfersListFilters>,
    toToggleFilterDrawer: boolean = true,
    nextPage: number = 1,
    totalPages: number = 1,
  ) => {
    modifyFavouriteTransferListDataFilterDrawer(filters);
    toToggleFilterDrawer && toggleFavouriteTransfersFilterDrawer();
    const newFilters = [
      { key: 'name', value: filters?.name },
      { key: 'debitAccountNumber', value: filters?.debitAccountNumber },
      { key: 'creditAccountNumber', value: filters?.creditAccountNumber },
      { key: 'amountFrom', value: filters.amountFrom ? parseFloat(filters.amountFrom) : undefined },
      { key: 'amountTo', value: filters.amountTo ? parseFloat(filters.amountTo) : undefined },
      { key: 'currency', value: filters?.currency },
    ].reduce((acc, { key, value }) => {
      if (value) {
        acc[key] = value;
      }
      return acc;
    }, {} as GenericObject);
    dispatch(
      action(FavouriteTransfersActionTypes.SetFavouriteTransfersListFilter, {
        filters: {
          ...newFilters,
          pageOffset: filters.pageSize ? Math.min(Math.max(nextPage - 1, 0), totalPages) : 0,
          pageSize:
            filters.pageOffset! + 2 === totalPages
              ? favouriteTransfersListData?.totalResults! % state.favouriteTransfersListFilters.pageSize!
              : 10,
        },
        timestamp: new Date().toISOString(),
      }),
    );
  };

  return (
    <FavouriteTransfersContext.Provider
      value={{
        ...state,
        favouriteTransfersListData,
        favouriteTransfersRefetch,
        currencies,
        errorState: {
          favouriteTransfersDataError,
          editFavouriteTransferError,
          deleteFavouriteTransferError,
          currenciesError,
        },
        loadingState: {
          isLoadingFavouriteTransfersList,
          isLoadingEditFavouriteTransfer,
          isLoadingDeleteFavouriteTransfer,
          isLoadingCurrencies,
        },
        editFavouriteTransferData,
        deleteFavouriteTransferData,
        onFavouriteTransferAction,
        onEditFavouriteTransfer,
        onDeleteFavouriteTransfer,
        onShareFavouriteTransfer,
        onUseTemplateFavouriteTransfer,
        onFavouriteTransferListFiltersChange,
        toggleFavouriteTransfersFilterDrawer,
        setFavouriteTransfersFilterDrawerClose,
        onResetFavouriteTransferListFilters,
        onResetSelectedFavouriteTransfer,
      }}>
      <FavouriteTransfersDispatchContext.Provider value={dispatch}>
        {children}
      </FavouriteTransfersDispatchContext.Provider>
    </FavouriteTransfersContext.Provider>
  );
};

export { FavouriteTransfersProvider };
